import React, { useState, useMemo } from "react";
import {
  GridComponent,
  ColumnsDirective,
  ColumnDirective,
  Inject,
  Page,
  Sort,
} from "@syncfusion/ej2-react-grids";
import {
  Box,
  Typography,
  TextField,
  Stack,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { PhysicianGroupType } from "../../../../services/ConfigurationService/types/physicianGroupType";
import { useDeletePhysicianGroupMutation } from "../../../../services/ConfigurationService";
import { AddPhysicianGroupDialog } from "../dialogs/AddPhysicianGroupDialog";
import { EditPhysicianGroupDialog } from "../dialogs/EditPhysicianGroupDialog";

interface PhysicianGroupGridProps {
  groups: PhysicianGroupType[];
  company: string;
  onViewMembers?: (group: PhysicianGroupType) => void;
}

export function PhysicianGroupGrid({ groups, company, onViewMembers }: PhysicianGroupGridProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [editGroup, setEditGroup] = useState<PhysicianGroupType | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<PhysicianGroupType | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [deletePhysicianGroup] = useDeletePhysicianGroupMutation();

  const filteredGroups = useMemo(() => {
    if (!searchTerm.trim()) return groups;
    const lower = searchTerm.toLowerCase();
    return groups.filter(
      (g) =>
        g.id?.toLowerCase().includes(lower) ||
        g.name?.toLowerCase().includes(lower) ||
        g.city?.toLowerCase().includes(lower)
    );
  }, [groups, searchTerm]);

  const handleDelete = async (group: PhysicianGroupType) => {
    setConfirmDelete(group);
  };

  const confirmDeleteAction = async () => {
    if (!confirmDelete) return;
    try {
      await deletePhysicianGroup({ id: confirmDelete.id }).unwrap();
    } catch (err: any) {
      const msg = err?.data?.message || err?.data || "Cannot delete group. It may still have assigned physicians.";
      setErrorMessage(typeof msg === "string" ? msg : JSON.stringify(msg));
    }
    setConfirmDelete(null);
  };

  const actionTemplate = (props: PhysicianGroupType) => (
    <Stack direction="row" spacing={0.5}>
      <IconButton size="small" title="Edit" onClick={() => setEditGroup(props)}>
        <EditIcon fontSize="small" />
      </IconButton>
      <IconButton size="small" title="View Members" onClick={() => onViewMembers?.(props)}>
        <ViewIcon fontSize="small" />
      </IconButton>
      <IconButton size="small" title="Delete" onClick={() => handleDelete(props)}>
        <DeleteIcon fontSize="small" color="error" />
      </IconButton>
    </Stack>
  );

  return (
    <Box>
      {/* Toolbar */}
      <Box sx={{ mb: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1, mr: 2 }}>
          <SearchIcon sx={{ color: "#64748b" }} />
          <TextField
            fullWidth
            size="small"
            placeholder="Search by ID, name, or city..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#ffffff",
                "& fieldset": { borderColor: "#e2e8f0" },
                "&:hover fieldset": { borderColor: "#3b82f6" },
                "&.Mui-focused fieldset": { borderColor: "#3b82f6" },
              },
            }}
          />
          {searchTerm && (
            <IconButton size="small" onClick={() => setSearchTerm("")} sx={{ color: "#64748b" }}>
              <ClearIcon fontSize="small" />
            </IconButton>
          )}
        </Stack>
        <AddPhysicianGroupDialog company={company} />
      </Box>

      {/* Grid */}
      {filteredGroups.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 8 }}>
          <Typography variant="h6" sx={{ color: "#64748b", mb: 1 }}>
            {searchTerm ? "No groups match your search" : "No physician groups found"}
          </Typography>
          <Typography variant="body2" sx={{ color: "#94a3b8" }}>
            {searchTerm ? `Try adjusting your search: "${searchTerm}"` : "Add a physician group to get started"}
          </Typography>
        </Box>
      ) : (
        <GridComponent
          dataSource={filteredGroups}
          height={500}
          allowPaging={true}
          allowSorting={true}
          pageSettings={{ pageSize: 25, pageSizes: [10, 25, 50] }}
          style={{ border: "1px solid #e2e8f0", borderRadius: "8px", backgroundColor: "#ffffff" }}
          enableHover={true}
        >
          <ColumnsDirective>
            <ColumnDirective field="id" headerText="ID" width="120" textAlign="Left" />
            <ColumnDirective field="name" headerText="Name" width="200" textAlign="Left" />
            <ColumnDirective field="phone" headerText="Phone" width="130" textAlign="Left" />
            <ColumnDirective field="city" headerText="City" width="130" textAlign="Left" />
            <ColumnDirective field="state" headerText="State" width="80" textAlign="Left" />
            <ColumnDirective
              headerText="Actions"
              width="140"
              textAlign="Center"
              template={actionTemplate}
            />
          </ColumnsDirective>
          <Inject services={[Page, Sort]} />
        </GridComponent>
      )}

      {/* Edit Dialog */}
      {editGroup && (
        <EditPhysicianGroupDialog
          group={editGroup}
          open={true}
          onClose={() => setEditGroup(null)}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>Delete Physician Group</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete &quot;{confirmDelete?.name}&quot; ({confirmDelete?.id})?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={confirmDeleteAction}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Error Snackbar */}
      <Snackbar
        open={!!errorMessage}
        autoHideDuration={6000}
        onClose={() => setErrorMessage(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="error" onClose={() => setErrorMessage(null)}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}
